import { createElement, useState, useRef, useEffect } from 'react';
import { SearchFieldProps } from './';
import { Button, ButtonVariant } from '../Button';
import { Icon } from '../Icon';
import { BaseInput } from '../Input/__internal__/BaseInput';
import { isNullish } from '@sas/nova-core/typeguards';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { useDirectSpeech } from '../DirectSpeech/DirectSpeechProvider';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { useGetGlobalSetting } from '../Settings/GlobalSettingsProvider';
import { GlobalSettingsKeys } from '../Settings/GlobalSettingsKeys';
import bundle from './NovaComponents-SearchField-gui-icu.properties';

import { setRef } from '../../util/ref-util';
import { postfixTestId } from '../../util/testId';
import { Search } from '@sas/react-svg';

import cx from 'classnames';
import './SearchField.css';
import cn from './SearchField.css.json';

const useTextLocalizer = createUseTextLocalizer(bundle);

export const SearchField = (props: SearchFieldProps) => {
  const [isActive, setIsActive] = useState<boolean>(false);
  const dt = useDesignTokens();
  const tokens = {
    bd: dt.stateInputNova.normal.bd,
    bg: dt.stateInputNova.normal.bg,
    fg: dt.stateInputNova.normal.fg,
    'hover-bd': dt.stateInputNova.hover.bd,
    'hover-bg': dt.stateInputNova.hover.bg,
    'hover-fg': dt.stateInputNova.hover.fg,
    'active-bd': dt.stateInputNova.active.bd,
    'active-bg': dt.stateInputNova.active.bg,
    'active-fg': dt.stateInputNova.active.fg,
    'disabled-fg': dt.stateInputNova.disabled.fg,
    'disabled-bd': dt.stateInputNova.disabled.bd,
    'disabled-bg': dt.stateInputNova.disabled.bg,
    'spacing-xs': dt.spacing.core.xs,
  };

  const disableEmptySearch = props.disableEmptySearch ?? false;

  const isCustomFocusTurnedOn = useGetGlobalSetting(GlobalSettingsKeys.FOCUS_OVERRIDE) === 'true';

  // Outline Focus Indicator
  let outline;
  let outlineOffset;

  if (dt.themeVariant !== 'high-contrast' && !isCustomFocusTurnedOn && !props.disabled) {
    outline = 'none';
  } else {
    outline = 'var(--focus-light-bg)';
    outlineOffset = 'calc(-1 * var(--focus-width) - 2px)';
  }

  const style = {
    ...renderInlineCssVars(tokens, 'searchfield'),
    ...renderInlineCssVars({ outline, 'outline-offset': outlineOffset }, 'searchfield-focus'),
  };

  const localize = useTextLocalizer();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const shouldSearchAnnounce = useRef(false); // used for search mode, not filter mode

  let narratorMessage = '';
  const numberOfResults = props.numberOfResults;
  if (!isNullish(numberOfResults)) {
    if (numberOfResults === 0) {
      narratorMessage = localize('narrator.noresults.msg');
    } else if (numberOfResults === 1) {
      narratorMessage = localize('narrator.result.msg');
    } else {
      narratorMessage = localize(
        props.numberOfResultsIndeterminant
          ? 'narrator.results.indeterminant.msg'
          : 'narrator.results.msg',
        {
          count: numberOfResults.toString(),
        }
      );
    }
  }

  const directSpeech = useDirectSpeech();
  useEffect(() => {
    // Should only announce for the following conditions when there are numberOfResults:
    // 1.  Always announce in filter mode when search field has focus
    // 2.  When search mode, only announce when empty search is enabled and there is no value,
    //     or when there is a value in the search field and the search button is pressed
    const shouldAnnounce =
      (props.filterMode || (!disableEmptySearch && props.value === '') || props.value !== '') &&
      !isNullish(numberOfResults);

    if (shouldAnnounce) {
      if (props.filterMode && isActive) {
        // Always announce in filterMode when focused
        directSpeech?.notify(narratorMessage);
      } else if (shouldSearchAnnounce.current && isActive) {
        // When search mode, only announce if search event has fired
        directSpeech?.notify(narratorMessage);
        shouldSearchAnnounce.current = false;
      }
    }
  }, [
    directSpeech,
    isActive,
    narratorMessage,
    numberOfResults,
    props.filterMode,
    props.value,
    shouldSearchAnnounce,
    disableEmptySearch,
  ]);

  useEffect(() => {
    const handleBlur = () => {
      shouldSearchAnnounce.current = false;
      setIsActive(false);
    };

    const handleInputKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (input?.value && input.value.length > 0) {
          props.onChange({ value: '' });
          e.stopPropagation();
        }
      }
    };

    const searchButton = searchButtonRef.current;
    searchButton?.addEventListener('blur', handleBlur);
    const input = inputRef.current;
    input?.addEventListener('keydown', handleInputKeyDown);
    return () => {
      searchButton?.removeEventListener('blur', handleBlur);
      input?.removeEventListener('keydown', handleInputKeyDown);
    };
  });

  const getLandmarkLabel = () => {
    if (!props.filterMode) {
      return props.landmarkLabel ?? localize('searchField.landmark.search.label');
    }
  };

  return (
    <div
      data-testid={props.testId}
      className={cx(props.className, cn.form, props.disabled && cn.disabled, isActive && cn.active)}
      style={{
        ...style,
        ...props.style,
      }}
      role={props.filterMode ? '' : 'search'}
      data-landmark-label={getLandmarkLabel()}
    >
      {props.filterMode ? (
        <div className={cn.searchButtonContainer}>
          <Icon
            className={cn.searchButton}
            icon={Search}
            tooltip=""
            ariaLabel=""
            ariaHidden
            testId={postfixTestId(props.testId, 'filterIcon')}
          />
        </div>
      ) : null}
      <BaseInput
        ref={(element) => {
          inputRef.current = element;
          setRef(props.inputRef, element);
        }}
        placeholder={
          props.placeholder ??
          (props.filterMode
            ? localize('searchField.placeholder.filter.label')
            : localize('searchField.placeholder.search.label'))
        }
        className={cn.searchInput}
        onChange={(e) => {
          props.onChange({ value: e.value });
          if (props.filterMode) {
            props.onCommit?.({ value: e.value });
            return;
          }
          if (!disableEmptySearch) {
            if (!e.value || e.value === '') {
              props.onCommit?.({ value: e.value });
              shouldSearchAnnounce.current = true;
            }
          }
          if (e.value === '') {
            setTimeout(() => inputRef.current?.focus(), 0);
          }
          if (!props.filterMode && shouldSearchAnnounce.current) {
            shouldSearchAnnounce.current = false;
          }
        }}
        onCommit={(e) => {
          if (props.disabled) return;
          if (e.reason === 'enter') {
            props.onCommit?.({ value: e.value });
            shouldSearchAnnounce.current = true;
          }
        }}
        value={props.value}
        disabled={props.disabled}
        onBlur={() => {
          setIsActive(false);
          if (shouldSearchAnnounce.current && props.filterMode)
            shouldSearchAnnounce.current = false;
          props.onBlur?.();
        }}
        onFocus={() => {
          setIsActive(true);
          props.onFocus?.();
        }}
        ariaLabel={
          props.ariaLabel ??
          (props.filterMode
            ? localize('searchField.input.filter.aria')
            : localize('searchField.input.search.aria'))
        }
        maxLength={props.maxLength}
        testId={postfixTestId(props.testId, 'searchInput')}
        commitOnEnterInDialog={props.commitOnEnterInDialog ?? true}
        showClearButton={props.value !== ''}
        clearButtonTooltip={
          props.clearButtonTooltip ??
          (props.filterMode
            ? localize('searchField.clearButton.filter.tooltip.label')
            : localize('searchField.clearButton.search.tooltip.label'))
        }
        clearButtonAriaLabel={
          props.clearButtonAriaLabel ??
          (props.filterMode
            ? localize('searchField.clearButton.filter.aria')
            : localize('searchField.clearButton.search.aria'))
        }
        clearButtonTokens={() => ({
          fg: props.disabled ? dt.stateStandardNova.disabled.fg : dt.c.colorTextDefault,
          filter: 'none',
          bg: 'transparent',
        })}
      />
      {props.filterMode ? null : (
        <Button
          ref={searchButtonRef}
          variant={ButtonVariant.Icon}
          className={cn.searchButton}
          disabled={props.disabled}
          icon={{
            icon: Search,
          }}
          tooltip={
            props.searchButtonTooltip ??
            localize('searchField.searchButton.startSearch.tooltip.label')
          }
          ariaLabel={
            props.searchButtonAriaLabel ?? localize('searchField.searchButton.startSearch.aria')
          }
          onPress={() => {
            props.onCommit?.({ value: props.value });
            shouldSearchAnnounce.current = true;
            setIsActive(true);
          }}
          testId={postfixTestId(props.testId, 'searchButton')}
          tokens={() => ({
            fg: props.disabled ? dt.stateStandardNova.disabled.fg : dt.c.colorTextDefault,
            filter: 'none',
            bg: 'transparent',
          })}
        />
      )}
    </div>
  );
};
